import React, { useState, useEffect } from "react";
import { useSupabase } from "../../contexts/SupabaseContext";
import { Button } from "../ui/button";
import { Alert, AlertDescription } from "../ui/alert";
import { useBusiness } from "../../contexts/BusinessContext";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const VITE_EMAIL_DOMAIN = import.meta.env.VITE_EMAIL_DOMAIN || "gitbooked.com";
const VITE_TEST_RECIPIENT = import.meta.env.VITE_TEST_RECIPIENT;

export function SendGridSettings() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [businessEmail, setBusinessEmail] = useState<string>("");
  const supabase = useSupabase();
  const { businessId } = useBusiness();

  useEffect(() => {
    const generateBusinessEmail = async () => {
      if (!businessId) return;

      const { data, error } = await supabase
        .from("businesses")
        .select("business_domain_email")
        .eq("id", businessId)
        .single();

      if (error) {
        console.error("Error fetching business domain email:", error);
        return;
      }

      if (data?.business_domain_email) {
        setBusinessEmail(data.business_domain_email);
      }
    };

    generateBusinessEmail();
  }, [businessId, supabase]);

  const handleTestEmail = async () => {
    if (!businessId) {
      setError("Business ID not found");
      return;
    }

    if (!businessEmail) {
      setError("Business email not configured");
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      if (!VITE_TEST_RECIPIENT) {
        setError("Test recipient not configured");
        return;
      }

      const { error } = await supabase.functions.invoke("send-email", {
        body: {
          to: VITE_TEST_RECIPIENT,
          subject: "Test Email from Booking App",
          text: "This is a test email to verify SendGrid integration.",
          html: "<p>This is a test email to verify SendGrid integration.</p>",
          businessId,
          from: businessEmail,
        },
      });

      if (error) throw error;

      setSuccess(true);
    } catch (error) {
      console.error("Error sending test email:", error);
      setError(
        "Failed to send test email. Please check your SendGrid configuration."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-medium">SendGrid Settings</h2>
          <p className="text-sm text-gray-500">
            SendGrid is configured using environment variables. Test your email
            configuration below.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="businessEmail">Business Email</Label>
          <Input
            id="businessEmail"
            value={businessEmail}
            readOnly
            className="bg-gray-50"
            placeholder="Generating business email..."
          />
          <p className="text-sm text-gray-500">
            This email will be used as the sender address for all emails from
            your business. Domain: {VITE_EMAIL_DOMAIN}
          </p>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert>
            <AlertDescription>Test email sent successfully</AlertDescription>
          </Alert>
        )}

        <Button
          onClick={handleTestEmail}
          disabled={isLoading || !businessId || !businessEmail}
          className="w-full"
        >
          {isLoading ? "Sending..." : "Send Test Email"}
        </Button>
      </div>
    </div>
  );
}
